import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from '../../../utils/api';

export interface ApplicantScholarshipApplication {
  id: number;
  institutionShortName: string;
  applicantId: string;
  applicationNumber: string;
  applicantName: string;
  originalFileName: string;
  submittedFileName: string;
  filePath: string;
  status: number;
  statusLabel: string;
  adminRemarks: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  updatedAt: string;
  currentStageOrder?: number;
  stageName?: string;
  scholarshipApplicationId?: number;
  scholarshipPercent?: number;
}

export interface ApplicantScholarshipApplicationResponse {
  statusCode: number;
  message: string;
  data: ApplicantScholarshipApplication;
}

export const GET_APPLICANT_SCHOLARSHIP_APPLICATION_QUERY_KEY = ['get-applicant-scholarship-application'];

export const useGetApplicantScholarshipApplicationQuery = (applicantId?: string | number) => {
  const fetchApplicantScholarshipApplication = async (): Promise<ApplicantScholarshipApplicationResponse> => {
    return (await api.get(`/admin/scholarship/${applicantId}`))?.data;
  };

  const { data, isLoading, isError, error, refetch } = useQuery<
    ApplicantScholarshipApplicationResponse,
    AxiosError<ApiErrorResponse>
  >({
    queryKey: [...GET_APPLICANT_SCHOLARSHIP_APPLICATION_QUERY_KEY, applicantId],
    queryFn: fetchApplicantScholarshipApplication,
    enabled: Boolean(applicantId),
  });

  const errorMessage =
    error?.response?.data?.message || error?.message || 'Failed to fetch scholarship application';

  return {
    data,
    applicantScholarshipApplicationData: data?.data,
    scholarshipApplicationData: data?.data,
    isLoading,
    isApplicantScholarshipApplicationLoading: isLoading,
    isScholarshipApplicationLoading: isLoading,
    isError,
    isApplicantScholarshipApplicationError: isError,
    isScholarshipApplicationError: isError,
    errorMessage,
    applicantScholarshipApplicationErrorMessage: errorMessage,
    scholarshipApplicationErrorMessage: errorMessage,
    refetch,
  };
};
